import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, ChevronLeft, ChevronRight, Play, ImageOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL || "";

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  // For relative URLs like /api/uploads/xxx, prepend base or use as-is
  return BASE ? `${BASE}${url}` : url;
}

function GalleryItem({ item }) {
  const [error, setError] = useState(false);
  const isVideo = item.type === "video";

  if (error) {
    return (
      <div className="w-full aspect-[4/3] bg-slate-100 rounded-2xl flex items-center justify-center">
        <ImageOff className="w-8 h-8 text-slate-300" />
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.06)]">
      {isVideo ? (
        <>
          <video
            src={item.src}
            autoPlay
            muted
            loop
            playsInline
            className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setError(true)}
          />
          <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm">
            <Play className="w-3 h-3 text-white" />
          </div>
        </>
      ) : (
        <img
          src={item.src}
          alt={item.alt}
          className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={() => setError(true)}
        />
      )}
      <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/20 transition-colors duration-300" />
    </div>
  );
}

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(r => {
      const p = r.data.map(photo => ({
        ...photo,
        src: resolveUrl(photo.image_url),
        alt: photo.caption,
        type: photo.type || "image",
      }));
      setPhotos(p);
    }).catch(() => {});
  }, []);

  const prev = () => setLightbox((i) => (i === 0 ? photos.length - 1 : i - 1));
  const next = () => setLightbox((i) => (i === photos.length - 1 ? 0 : i + 1));

  if (photos.length === 0) return null;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 px-6 bg-slate-50 relative overflow-hidden">
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-navy/3 blur-3xl" />
      <div className="max-w-7xl mx-auto relative">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-navy/5 text-navy text-xs font-body font-bold uppercase tracking-[0.2em] mb-6">
            <Camera className="w-3.5 h-3.5" />
            Gallery
          </span>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-navy mb-4">
            Church Moments
          </h2>
          <p className="font-body text-slate-700 text-base max-w-lg mx-auto">
            Glimpses of worship, fellowship, and the power of God at Dominion Parish.
          </p>
        </motion.div>

        <div className="columns-2 md:columns-3 gap-4 max-w-5xl mx-auto">
          {photos.map((photo, i) => (
            <motion.div
              key={photo.id || i}
              className="mb-4 break-inside-avoid cursor-pointer group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(i * 0.08, 0.5), duration: 0.6 }}
              onClick={() => setLightbox(i)}
              data-testid={`gallery-img-${i}`}
            >
              <GalleryItem item={photo} />
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="lightbox"
          >
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"><ChevronLeft className="w-6 h-6" /></button>

            {photos[lightbox].type === "video" ? (
              <video
                key={lightbox}
                src={photos[lightbox].src}
                controls
                autoPlay
                playsInline
                className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <motion.img
                key={lightbox}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                src={photos[lightbox].src}
                alt={photos[lightbox].alt}
                className="max-h-[85vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"><ChevronRight className="w-6 h-6" /></button>
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"><X className="w-5 h-5" /></button>
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-body text-white/70 text-sm">{lightbox + 1} / {photos.length}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
